import { Injectable, inject } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { EnvService } from '@src/app/modules/env';
import { UserService } from '@src/core/services';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramPermService {
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly env = inject(EnvService);
  private readonly userService = inject(UserService);

  readonly allowCollectionEditing$ = new BehaviorSubject(false);
  readonly allowCollectionDeleting$ = new BehaviorSubject(false);
  readonly allowProductRequestEditing$ = new BehaviorSubject(false);
  readonly allowProductRequestDeleting$ = new BehaviorSubject(false);

  private collectionParentOrganisationId?: string;
  private productRequestCreatorId?: string;

  private permissions = this.ngxPermissionsService.getPermissions();

  constructor() {
    this.userService.authUser$.subscribe(() => {
      this.loadCollectionPermissions(this.collectionParentOrganisationId);
      this.loadProductRequestPermissions(this.productRequestCreatorId);
    });
  }

  get allowedCreating(): boolean {
    return 'loyaltyProgramCreating' in this.permissions && this.isNotCordova;
  }

  get allowedEditing(): boolean {
    return 'loyaltyProgramEditing' in this.permissions && this.isNotCordova;
  }

  get allowedDeleting(): boolean {
    return 'loyaltyProgramDeleting' in this.permissions;
  }

  get allowedPublishing(): boolean {
    return 'loyaltyProgramPublishing' in this.permissions;
  }

  get allowedUnpublishing(): boolean {
    return 'loyaltyProgramUnpublishing' in this.permissions;
  }

  get allowedAllStatisticsViewing(): boolean {
    return 'loyaltyProgramAllStatisticsViewing' in this.permissions;
  }

  get allowedCollectionStatisticsViewing(): boolean {
    return 'loyaltyProgramCollectionStatisticsViewing' in this.permissions;
  }

  get allowedProductStatisticsViewing(): boolean {
    return 'loyaltyProgramProductStatisticsViewing' in this.permissions;
  }

  get allowedOnlyYourProductStatisticsViewing(): boolean {
    return 'loyaltyProgramOnlyYourProductStatisticsViewing' in this.permissions;
  }

  get allowedOnlyYourOrganisationProductEditing(): boolean {
    return 'loyaltyProgramOnlyYourOrganisationProductEditing' in this.permissions;
  }

  get allowedOnlyYourOrganisationProductUnpublishing(): boolean {
    return 'loyaltyProgramOnlyYourOrganisationProductUnpublishing' in this.permissions;
  }

  get showOnlyLoyaltyProgram(): boolean {
    return 'showOnlyLoyaltyProgram' in this.permissions;
  }

  get allowedProductCodesExport(): boolean {
    return 'loyaltyProgramProductCodesExport' in this.permissions;
  }

  get allowedOnlyYourOrganisationProductCodesExport(): boolean {
    return 'loyaltyProgramOnlyYourOrganisationProductCodesExport' in this.permissions;
  }

  get allowActiveCollectionsViewing(): boolean {
    return 'loyaltyProgramActiveCollectionsViewing' in this.permissions;
  }

  get allowCollectionEditing(): boolean {
    return 'loyaltyProgramCollectionEditing' in this.permissions;
  }

  get allowCollectionDeleting(): boolean {
    return 'loyaltyProgramCollectionDeleting' in this.permissions;
  }

  // TODO: isNotCordova - временное решение для задачи https://redmine.tbnsoft.ru/issues/9437
  get isNotCordova() {
    return !this.env.isCordova;
  }

  loadCollectionPermissions(collectionParentOrganisationId?: string) {
    this.collectionParentOrganisationId = collectionParentOrganisationId;

    const authUser = this.userService.authUser$.value;

    this.allowCollectionEditing$.next(
      'loyaltyProgramCollectionEditing' in this.permissions &&
        !!collectionParentOrganisationId &&
        collectionParentOrganisationId === authUser?.parentOrganisationId,
    );

    this.allowCollectionDeleting$.next(
      'loyaltyProgramCollectionDeleting' in this.permissions &&
        !!collectionParentOrganisationId &&
        collectionParentOrganisationId === authUser?.parentOrganisationId,
    );
  }

  loadProductRequestPermissions(productRequestCreatorId?: string) {
    this.productRequestCreatorId = productRequestCreatorId;

    const authUser = this.userService.authUser$.value;

    this.allowProductRequestEditing$.next(
      'loyaltyProgramProductRequestEditing' in this.permissions ||
        ('loyaltyProgramProductRequestCommentsEditing' in this.permissions &&
          !!productRequestCreatorId &&
          productRequestCreatorId === authUser?.id),
    );

    this.allowProductRequestDeleting$.next(
      'loyaltyProgramProductRequestDeleting' in this.permissions ||
        ('loyaltyProgramOnlyYourProductRequestDeleting' in this.permissions &&
          !!productRequestCreatorId &&
          productRequestCreatorId === authUser?.id),
    );
  }
}
